//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { convertStylesToStyled } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { Assets } from '@/redux/slices/slice_types';

interface JobRequirementsProps {
  sources: Assets;
  size?: number;
}

export const JobRequirements = ({ sources, size = 45 }: JobRequirementsProps) => {
  if (Object.keys(sources).length === 0) {
    return (
      <StyledJobsRequirementsGrid container alignItems="center" justifyContent="center" style={{ height: size }}>
        <StyledEmptyAssetsText>-</StyledEmptyAssetsText>
      </StyledJobsRequirementsGrid>
    );
  }

  const { total, received, progress } = sources;

  const isMissing = total < 1;
  const isComplete = !isMissing && total === received;
  const pendingCount = total - received;

  return (
    <StyledJobsRequirementsGrid container alignItems="center" justifyContent="center" style={{ height: size }}>
      {!isComplete && !isMissing && <StyledCircularProgress variant="determinate" value={100} size={size} thickness={4} />}
      {!isMissing && progress !== 0 && (
        <StyledCircularProgressComplete variant="determinate" value={progress} size={size} thickness={4} complete={String(isComplete)} />
      )}
      <SourcesDiv style={{ width: size }}>
        {isMissing ? (
          <Tooltip title="No assets found.">
            <StyledMissingIcon icon="error" />
          </Tooltip>
        ) : isComplete ? (
          <StyledSuccessIcon icon="complete" />
        ) : (
          <Tooltip title={`${pendingCount} Pending asset${pendingCount !== 1 ? 's' : ''}`}>
            <CountDiv style={{ width: size - 10 }}>
              {received}/{total}
            </CountDiv>
          </Tooltip>
        )}
      </SourcesDiv>
    </StyledJobsRequirementsGrid>
  );
};

export default JobRequirements;

const StyledJobsRequirementsGrid = styled(Grid)`
  overflow: hidden;
  position: relative;
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.SourcesProgress)}
`;

const StyledEmptyAssetsText = styled(Typography)`
  color: ${({ theme }) => theme.palette.default.main};
  font-weight: bold;
`;

const StyledCircularProgress = styled(CircularProgress)`
  position: absolute;
  color: ${({ theme }) => theme.palette.warning.light};
`;

const SourcesDiv = styled.div`
  font-weight: 600;
  font-size: 0.785rem;
  position: absolute;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CountDiv = styled.div`
  opacity: 0.9;
  font-size: 0.9rem;
  letter-spacing: -0.5;
`;

const StyledMissingIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  min-width: 26px;
  min-height: 26px;
  font-size: 1.66rem;
`;

const StyledSuccessIcon = styled(PolarisIcon)`
  color: ${(props) => props.theme.palette.success.main};
  font-size: 1.6rem;
  min-width: 26px;
  min-height: 26px;
`;

const StyledCircularProgressComplete = styled(CircularProgress)<{ complete?: string }>`
  color: ${(props) => props.theme.palette.success.main};

  ${(props) =>
    props.complete === String(true)
      ? `
    color: transparent;
    border-radius: 25px;
  `
      : null};
`;
